import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { RefreshCw, DollarSign, Download, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { VAULT_ADDRESS } from '../config/constants';
import { TOKENS as SUPPORTED_TOKENS } from '../config/constantsV2';
import { TokenLogos } from '../assets/tokenLogos';

// Vault ABI for owner functions
const VAULT_ABI = [
  "function owner() view returns (address)",
  "function withdrawToken(address token, uint256 amount) external",
  "function withdrawNative(uint256 amount) external",
  "function getTokenBalance(address token) view returns (uint256)",
  "function swapperContract() view returns (address)",
  "function setSwapperContract(address _swapper) external"
];

const VaultAdmin = ({ account, signer }) => {
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [balances, setBalances] = useState({});
  const [nativeBalance, setNativeBalance] = useState('0');
  const [withdrawing, setWithdrawing] = useState(null);
  const [currentSwapper, setCurrentSwapper] = useState('');
  const [newSwapperAddress, setNewSwapperAddress] = useState('');
  const [settingSwapper, setSettingSwapper] = useState(false);

  // Check if connected wallet is the owner
  useEffect(() => {
    checkOwnership();
  }, [account, signer]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkOwnership = async () => {
    if (!account || !signer) {
      setLoading(false);
      return;
    }

    try {
      const vaultContract = new ethers.Contract(VAULT_ADDRESS, VAULT_ABI, signer);
      const owner = await vaultContract.owner();
      const isCurrentUserOwner = owner.toLowerCase() === account.toLowerCase();
      setIsOwner(isCurrentUserOwner);
      
      if (isCurrentUserOwner) {
        await fetchBalances();
        const swapper = await vaultContract.swapperContract();
        setCurrentSwapper(swapper);
      }
    } catch (error) {
      console.error('Error checking ownership:', error);
      toast.error('Failed to verify ownership');
    } finally {
      setLoading(false);
    }
  };

  const fetchBalances = async () => {
    if (!signer) return;

    try {
      console.log('🏦 Fetching vault balances from:', VAULT_ADDRESS);
      const vaultContract = new ethers.Contract(VAULT_ADDRESS, VAULT_ABI, signer);
      
      // Get native FTM balance (using address(0))
      console.log('💰 Fetching native balance...');
      const nativeBal = await vaultContract.getTokenBalance(ethers.ZeroAddress);
      console.log('✅ Native balance wei:', nativeBal.toString());
      setNativeBalance(ethers.formatEther(nativeBal));

      // Get token balances
      console.log('🪙 Fetching token balances...');
      const tokenBalances = {};
      for (const [symbol, token] of Object.entries(SUPPORTED_TOKENS)) {
        if (token.address === ethers.ZeroAddress) continue; // Skip native FTM
        
        try {
          console.log(`  Fetching ${symbol} balance from ${token.address}...`);
          const balance = await vaultContract.getTokenBalance(token.address);
          console.log(`  ✅ ${symbol} balance wei:`, balance.toString());
          tokenBalances[symbol] = ethers.formatUnits(balance, token.decimals);
        } catch (error) {
          console.error(`❌ Error fetching ${symbol} balance:`, error);
          tokenBalances[symbol] = '0';
        }
      }
      
      setBalances(tokenBalances);
    } catch (error) {
      console.error('Error fetching balances:', error);
      toast.error('Failed to fetch vault balances');
    }
  };

  const setSwapper = async () => {
    if (!signer || !newSwapperAddress) return;

    if (!ethers.isAddress(newSwapperAddress)) {
      toast.error('Invalid contract address');
      return;
    }

    setSettingSwapper(true);
    try {
      const vaultContract = new ethers.Contract(VAULT_ADDRESS, VAULT_ABI, signer);
      const tx = await vaultContract.setSwapperContract(newSwapperAddress);
      toast.loading('Setting swapper contract...', { id: 'setSwapper' });
      await tx.wait();
      toast.success('Swapper contract updated successfully!', { id: 'setSwapper' });
      setCurrentSwapper(newSwapperAddress);
      setNewSwapperAddress('');
    } catch (error) {
      console.error('Set swapper error:', error);
      toast.error(`Failed to set swapper: ${error.message}`, { id: 'setSwapper' });
    } finally {
      setSettingSwapper(false);
    }
  };

  const withdrawToken = async (tokenAddress, symbol, decimals) => {
    if (!signer) return;

    setWithdrawing(symbol);
    try {
      const vaultContract = new ethers.Contract(VAULT_ADDRESS, VAULT_ABI, signer);
      
      // Get current balance
      const balance = await vaultContract.getTokenBalance(tokenAddress);
      
      if (balance.toString() === '0') {
        toast.error(`No ${symbol} balance to withdraw`);
        return;
      }

      // Withdraw all tokens
      const tx = await vaultContract.withdrawToken(tokenAddress, balance);
      
      toast.loading(`Withdrawing ${symbol}...`, { id: 'withdraw' });
      await tx.wait();
      
      toast.success(`Successfully withdrew ${ethers.formatUnits(balance, decimals)} ${symbol}`, { id: 'withdraw' });
      
      // Refresh balances
      await fetchBalances();
    } catch (error) {
      console.error('Withdrawal error:', error);
      toast.error(`Failed to withdraw ${symbol}: ${error.message}`, { id: 'withdraw' });
    } finally {
      setWithdrawing(null);
    }
  };

  const withdrawNative = async () => {
    if (!signer) return;

    setWithdrawing('FTM');
    try {
      const vaultContract = new ethers.Contract(VAULT_ADDRESS, VAULT_ABI, signer);
      
      // Get current native balance using getTokenBalance(address(0))
      const balance = await vaultContract.getTokenBalance(ethers.ZeroAddress);
      
      if (balance.toString() === '0') {
        toast.error('No FTM balance to withdraw');
        return;
      }

      // Withdraw all native tokens
      const tx = await vaultContract.withdrawNative(balance);
      
      toast.loading('Withdrawing FTM...', { id: 'withdraw' });
      await tx.wait();
      
      toast.success(`Successfully withdrew ${ethers.formatEther(balance)} FTM`, { id: 'withdraw' });
      
      // Refresh balances
      await fetchBalances();
    } catch (error) {
      console.error('Native withdrawal error:', error);
      toast.error(`Failed to withdraw FTM: ${error.message}`, { id: 'withdraw' });
    } finally {
      setWithdrawing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-white/60" />
        <span className="ml-2 text-white/60">Checking permissions...</span>
      </div>
    );
  }

  if (!isOwner) {
    return null; // Don't show anything if not owner
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white font-orbitron">Vault Admin</h2>
              <p className="text-sm text-white/60">Owner Dashboard</p>
            </div>
          </div>
          <button
            onClick={fetchBalances}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors duration-200"
            title="Refresh Balances"
          >
            <RefreshCw className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Contract Info */}
        <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
          <div className="flex items-center space-x-2 mb-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-sm font-medium text-white">Connected as Owner</span>
          </div>
          <p className="text-xs text-white/60 font-mono mb-2">
            Vault: {VAULT_ADDRESS}
          </p>
          <p className="text-xs text-white/60 font-mono">
            Current Swapper: {currentSwapper}
          </p>
        </div>

        {/* Set Swapper Contract */}
        <div className="mb-6 p-4 bg-blue-500/10 rounded-lg border border-blue-400/20">
          <h3 className="text-sm font-semibold text-blue-300 mb-3">Set Swapper Contract (V2)</h3>
          <p className="text-xs text-white/60 mb-3">
            Authorize SwapperV2 to collect fees: <span className="font-mono text-blue-300">0x26c1EB29C1BB140e44766764058311328Cd3cFf3</span>
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="0x..."
              value={newSwapperAddress}
              onChange={(e) => setNewSwapperAddress(e.target.value)}
              className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
            <button
              onClick={setSwapper}
              disabled={settingSwapper || !newSwapperAddress}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              {settingSwapper && <RefreshCw className="w-4 h-4 animate-spin" />}
              {settingSwapper ? 'Setting...' : 'Set Swapper'}
            </button>
          </div>
        </div>

        {/* Native FTM Balance */}
        <div className="mb-4">
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center space-x-3">
              <img src={TokenLogos.FTM} alt="FTM" className="w-8 h-8" />
              <div>
                <div className="font-semibold text-white">FTM</div>
                <div className="text-sm text-white/60">Native Token</div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-semibold text-white">
                {parseFloat(nativeBalance).toFixed(4)} FTM
              </div>
              <button
                onClick={withdrawNative}
                disabled={withdrawing === 'FTM' || parseFloat(nativeBalance) === 0}
                className="mt-1 px-3 py-1 text-xs bg-blue-500/20 hover:bg-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed border border-blue-400/50 text-blue-300 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-1"
              >
                {withdrawing === 'FTM' ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  <Download className="w-3 h-3" />
                )}
                <span>Withdraw</span>
              </button>
            </div>
          </div>
        </div>

        {/* Token Balances */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-white mb-3 font-orbitron">Token Balances</h3>
          
          {Object.entries(SUPPORTED_TOKENS).map(([symbol, token]) => {
            if (token.address === ethers.ZeroAddress) return null; // Skip native FTM
            
            const balance = balances[symbol] || '0';
            const hasBalance = parseFloat(balance) > 0;
            
            return (
              <div
                key={symbol}
                className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10"
              >
                <div className="flex items-center space-x-3">
                  <img src={token.logo} alt={symbol} className="w-8 h-8" />
                  <div>
                    <div className="font-semibold text-white">{symbol}</div>
                    <div className="text-sm text-white/60">{token.name}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-white">
                    {parseFloat(balance).toFixed(6)} {symbol}
                  </div>
                  <button
                    onClick={() => withdrawToken(token.address, symbol, token.decimals)}
                    disabled={withdrawing === symbol || !hasBalance}
                    className="mt-1 px-3 py-1 text-xs bg-blue-500/20 hover:bg-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed border border-blue-400/50 text-blue-300 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-1"
                  >
                    {withdrawing === symbol ? (
                      <RefreshCw className="w-3 h-3 animate-spin" />
                    ) : (
                      <Download className="w-3 h-3" />
                    )}
                    <span>Withdraw</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Warning */}
        <div className="mt-6 p-3 bg-yellow-500/10 border border-yellow-400/20 rounded-lg">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-300">
              <strong>Owner Only:</strong> This interface is only visible to the vault owner. 
              Withdrawals will transfer funds to your connected wallet.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VaultAdmin;